/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useReducer } from 'react';
import { v4 as uuid } from 'uuid';
import { useDispatch } from 'react-redux';
import * as R from 'ramda';
import { t } from '../../../../locales/i18n';
import { Config } from './config';
import { Entry } from './entry';
import { AddEntry } from './add-entry';
import { CalException } from './cal-exception';

import { Tooltip } from '../../../../components/widgets/tooltip';
import { sizes } from '../../../../themes/sizes';

import { saveQueueSettingsSaga } from '../../../../features/queue-settings';
import { BusinessHours as TBusinessHours, BHEntry } from '../../../../types/business-hours';
import { Button } from 'semantic-ui-react';
import styled from 'styled-components';
import { AddCalendarException } from './add-calendar-exception';
import { CalendarException } from '../../../../types/calendar-exception';

const Container = styled.section`
  display: grid;
`;

const ActionSegment = styled.section`
  display: grid;
  margin: ${sizes.margins.large} 0;
`;
const ConfigSegment = styled.section`
  display: grid;
`;
const ListSegment = styled.section`
  display: grid;
  margin: ${sizes.margins.large} 0;
`;
const AddEntitySegment = styled.section`
  display: grid;
  margin: ${sizes.margins.large} 0;
`;

export interface BusinessHoursProps {
  id: string;
  businessHours: TBusinessHours;
}

interface ReducerState extends TBusinessHours {
  isDirty?: boolean;
}

interface ConfigChangeAction {
  type: 'CONFIG_CHANGED';
  fieldName: string;
  value: string;
}
interface EntryDeleteAction {
  type: 'ENTRY_DELETED';
  entryId: string;
}
interface EntryAddAction {
  type: 'ENTRY_ADDED';
  entry: BHEntry;
}

interface CalExceptionDeleteAction {
  type: 'CAL_EXCEPTION_DELETED';
  calExceptionId: string;
}
interface CalExceptionAddAction {
  type: 'CAL_EXCEPTION_ADDED';
  calException: CalendarException;
}

interface EntryChangeAction {
  type: 'ENTRY_CHANGED';
  entryId: string;
  fieldName: string;
  value: string;
}

interface SaveRequestAction {
  type: 'SAVE_REQUESTED';
}

type ReducerAction =
  | ConfigChangeAction
  | EntryChangeAction
  | EntryDeleteAction
  | EntryAddAction
  | CalExceptionAddAction
  | CalExceptionDeleteAction
  | SaveRequestAction;

function businessHoursReducer(state: ReducerState, action: ReducerAction) {
  switch (action.type) {
    case 'SAVE_REQUESTED': {
      return { ...state, isDirty: false };
    }
    case 'CONFIG_CHANGED': {
      const newConfig = {
        ...state.config,
        [action.fieldName]: action.value
      };
      return { ...state, config: newConfig, isDirty: true };
    }
    case 'ENTRY_CHANGED': {
      const { entryId, fieldName, value } = action;
      const entries = state.entries;
      const idx = R.findIndex((entry: BHEntry) => entry.id === entryId)(entries);
      if (idx === -1) {
        return state;
      }
      const newEntries = R.update(idx, { ...entries[idx], [fieldName]: value })(entries);
      return { ...state, entries: newEntries, isDirty: true };
    }
    case 'ENTRY_DELETED': {
      const { entryId } = action;
      const entries = state.entries;

      const newEntries = R.reject((entry: BHEntry) => entry.id === entryId)(entries);
      return { ...state, entries: newEntries, isDirty: true };
    }
    case 'ENTRY_ADDED': {
      const { entry } = action;
      const entries = state.entries;
      const newEntries = R.append({ ...entry, id: uuid() })(entries);
      return { ...state, entries: newEntries, isDirty: true };
    }

    case 'CAL_EXCEPTION_DELETED': {
      const { calExceptionId } = action;
      const calExceptions = state.exceptions || [];

      const newEntries = R.reject((ce: CalendarException) => ce.id === calExceptionId)(calExceptions);
      return { ...state, exceptions: newEntries, isDirty: true };
    }
    case 'CAL_EXCEPTION_ADDED': {
      const { calException } = action;
      const calExceptions = state.exceptions || [];
      const newCalExceptions = R.append({ ...calException, id: uuid() })(calExceptions);
      return { ...state, exceptions: newCalExceptions, isDirty: true };
    }
  }
}

export function BusinessHours(props: BusinessHoursProps) {
  const reduxDispatch = useDispatch();
  const [state, reactDispatch] = useReducer(businessHoursReducer, props.businessHours);
  return (
    <Container>
      <ActionSegment>
        <Button
          onClick={() => {
            reactDispatch({ type: 'SAVE_REQUESTED' });
            reduxDispatch(
              saveQueueSettingsSaga({
                queueId: props.id,
                businessHours: state
              })
            );
          }}
          secondary
          disabled={!state.isDirty}
        >
          {t('button.applyCalendarChanges')}
        </Button>
      </ActionSegment>
      <ConfigSegment>
        <Config
          bhConfig={state.config}
          onChange={(fieldName, value) => reactDispatch({ type: 'CONFIG_CHANGED', fieldName, value })}
        />
      </ConfigSegment>

      <ListSegment>
        <h4>{t('queueSettingsPage.entries')}</h4>
        {(state.entries || []).map((entry: BHEntry) => {
          if (!entry.id) {
            entry.id = uuid();
          }
          return (
            <Entry
              onChange={(fieldName, value) =>
                reactDispatch({ type: 'ENTRY_CHANGED', entryId: entry.id || '', fieldName, value })
              }
              onDelete={() => reactDispatch({ type: 'ENTRY_DELETED', entryId: entry.id || '' })}
              key={entry.id}
              entry={entry}
            />
          );
        })}
      </ListSegment>
      <AddEntitySegment>
        <AddEntry onAdd={(entry) => reactDispatch({ type: 'ENTRY_ADDED', entry })} />
      </AddEntitySegment>

      <ListSegment>
        <h4>
          {t('queueSettingsPage.calendarExceptions')}
          <Tooltip tipKey="calExceptions" />
        </h4>
        {(state.exceptions || []).map((calException: CalendarException) => {
          if (!calException.id) {
            calException.id = uuid();
          }
          return (
            <CalException
              onDelete={() => reactDispatch({ type: 'CAL_EXCEPTION_DELETED', calExceptionId: calException.id || '' })}
              key={calException.id}
              calendarException={calException}
            />
          );
        })}
      </ListSegment>
      <AddEntitySegment>
        <AddCalendarException
          onAdd={(calException) => reactDispatch({ type: 'CAL_EXCEPTION_ADDED', calException: calException })}
        />
      </AddEntitySegment>
      <ActionSegment>
        <Button
          onClick={() => {
            reactDispatch({ type: 'SAVE_REQUESTED' });
            reduxDispatch(
              saveQueueSettingsSaga({
                queueId: props.id,
                businessHours: state
              })
            );
          }}
          secondary
          disabled={!state.isDirty}
        >
          {t('button.applyCalendarChanges')}
        </Button>
      </ActionSegment>
    </Container>
  );
}
